/**
 * PDF Annotation Viewer - Alpine.js Component
 *
 * Renders PDFs with annotation markers and click-to-comment functionality.
 * Uses PDF.js loaded via CDN (window.pdfjsLib).
 */

export default function pdfAnnotationViewer(config) {
    const scriptId = typeof config === 'string' ? config : null;
    if (typeof config === 'string') {
        const el = document.getElementById(config);
        config = el ? (() => { try { return JSON.parse(el.textContent); } catch (e) { return {}; } })() : {};
    }
    config = config || {};
    return {
        _scriptId: scriptId,
        // Configuration
        pdfUrl: config.url || null,
        comments: config.comments || [],
        mode: config.mode || 'view', // 'view', 'admin', 'user'
        uploadId: config.uploadId || null,
        csrfToken: config.csrfToken || '',
        refreshUrlEndpoint: config.refreshUrlEndpoint || null,
        urlExpiresAt: config.urlExpiresAt || null,

        // State
        pdfDoc: null,
        currentPage: 1,
        totalPages: 0,
        scale: 1.0,
        minScale: 0.5,
        maxScale: 3.0,
        isLoading: true,
        loadError: null,
        annotationMode: false,
        pendingAnnotation: null,
        selectedCommentId: null,

        // Canvas references
        canvas: null,
        ctx: null,
        markersContainer: null,

        // Rendering state
        renderTask: null,
        pageRendering: false,
        pageNumPending: null,

        async init() {
            // Wait for PDF.js to be available (max 5 seconds)
            if (!window.pdfjsLib) {
                let attempts = 0;
                while (!window.pdfjsLib && attempts < 50) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                    attempts++;
                }
            }

            this.canvas = this.$refs.canvas;
            this.ctx = this.canvas.getContext('2d');
            this.markersContainer = this.$refs.markers;

            if (this.pdfUrl) {
                await this.loadPdf();
            }

            // Set up URL refresh timer
            this.setupUrlRefresh();

            // Listen for external events
            this.$watch('pdfUrl', () => {
                if (this.pdfUrl) {
                    this.loadPdf();
                }
            });
        },

        async loadPdf() {
            this.isLoading = true;
            this.loadError = null;

            // Use globally loaded pdfjsLib (loaded via CDN in blade template)
            if (!window.pdfjsLib) {
                this.loadError = 'PDF.js library not loaded. Please refresh the page.';
                this.isLoading = false;
                return;
            }

            try {
                const loadingTask = window.pdfjsLib.getDocument({
                    url: this.pdfUrl,
                    cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/cmaps/',
                    cMapPacked: true,
                });

                const pdfDoc = await loadingTask.promise;
                this.$el._pdfDoc = pdfDoc;
                if (this._scriptId) {
                    const se = document.getElementById(this._scriptId);
                    if (se) se._pdfDoc = pdfDoc;
                }
                this.totalPages = pdfDoc.numPages;
                this.currentPage = 1;

                await this.renderPage(this.currentPage);
                this.isLoading = false;
            } catch (error) {
                console.error('Error loading PDF:', error);
                this.loadError = 'Failed to load PDF. Please try refreshing the page.';
                this.isLoading = false;
            }
        },

        async renderPage(pageNum) {
            let pdfDoc = this.$el && this.$el._pdfDoc;
            if (!pdfDoc && this._scriptId) {
                const se = document.getElementById(this._scriptId);
                pdfDoc = se && se._pdfDoc;
            }
            const canvas = this.$refs.canvas;
            if (!pdfDoc) return;
            if (!canvas) return;
            if (this.pageRendering) {
                this.pageNumPending = pageNum;
                return;
            }

            this.pageRendering = true;

            try {
                const page = await pdfDoc.getPage(pageNum);
                const viewport = page.getViewport({ scale: this.scale });

                const ctx = canvas.getContext('2d');
                canvas.height = viewport.height;
                canvas.width = viewport.width;

                const renderContext = {
                    canvasContext: ctx,
                    viewport: viewport,
                };

                if (this.$el._renderTask) {
                    this.$el._renderTask.cancel();
                }

                const renderTask = page.render(renderContext);
                this.$el._renderTask = renderTask;
                await renderTask.promise;
                this.$el._renderTask = null;
                this.pageRendering = false;

                this.renderMarkers();

                if (this.pageNumPending !== null) {
                    const pending = this.pageNumPending;
                    this.pageNumPending = null;
                    await this.renderPage(pending);
                }
            } catch (error) {
                if (error.name !== 'RenderingCancelledException') {
                    console.error('Error rendering page:', error);
                }
                this.pageRendering = false;
            }
        },

        renderMarkers() {
            const markersContainer = this.$refs.markers;
            if (!markersContainer) return;

            while (markersContainer.firstChild) {
                markersContainer.removeChild(markersContainer.firstChild);
            }

            const pageComments = this.comments.filter(comment => {
                if (!comment.location_data) return false;
                return comment.location_data.page === this.currentPage &&
                       comment.location_data.upload_id === this.uploadId;
            });

            pageComments.forEach((comment, index) => {
                const marker = this.createMarker(comment, index + 1);
                markersContainer.appendChild(marker);
            });

            if (this.pendingAnnotation && this.pendingAnnotation.page === this.currentPage) {
                const pendingMarker = this.createPendingMarker();
                markersContainer.appendChild(pendingMarker);
            }
        },

        createMarker(comment, number) {
            const marker = document.createElement('div');
            const isAdmin = comment.author_type === 'admin';
            const isSelected = this.selectedCommentId === comment.id;

            marker.className = `absolute w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold cursor-pointer transform -translate-x-1/2 -translate-y-1/2 transition-all duration-200 ${
                isSelected ? 'ring-2 ring-offset-2 ring-blue-500 scale-125' : 'hover:scale-110'
            } ${
                isAdmin ? 'bg-blue-500 text-white' : 'bg-green-500 text-white'
            }`;

            marker.style.left = `${comment.location_data.x * 100}%`;
            marker.style.top = `${comment.location_data.y * 100}%`;
            marker.textContent = number.toString();
            marker.title = `${isAdmin ? 'Admin' : 'User'} comment: ${comment.body.substring(0, 50)}...`;

            marker.addEventListener('click', (e) => {
                e.stopPropagation();
                this.selectComment(comment.id);
            });

            return marker;
        },

        createPendingMarker() {
            const marker = document.createElement('div');
            marker.className = 'absolute w-8 h-8 rounded-full flex items-center justify-center text-white font-bold cursor-pointer transform -translate-x-1/2 -translate-y-1/2 bg-orange-500 animate-pulse';
            marker.style.left = `${this.pendingAnnotation.x * 100}%`;
            marker.style.top = `${this.pendingAnnotation.y * 100}%`;

            // Create SVG icon using DOM methods
            const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            svg.setAttribute('class', 'w-4 h-4');
            svg.setAttribute('fill', 'none');
            svg.setAttribute('stroke', 'currentColor');
            svg.setAttribute('viewBox', '0 0 24 24');

            const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            path.setAttribute('stroke-linecap', 'round');
            path.setAttribute('stroke-linejoin', 'round');
            path.setAttribute('stroke-width', '2');
            path.setAttribute('d', 'M12 4v16m8-8H4');

            svg.appendChild(path);
            marker.appendChild(svg);
            marker.title = 'New comment location';

            return marker;
        },

        handleCanvasClick(event) {
            if (!this.annotationMode) return;
            const canvas = this.$refs.canvas;
            if (!canvas) return;

            const rect = canvas.getBoundingClientRect();
            const x = (event.clientX - rect.left) / rect.width;
            const y = (event.clientY - rect.top) / rect.height;

            this.pendingAnnotation = {
                page: this.currentPage,
                x: Math.max(0, Math.min(1, x)),
                y: Math.max(0, Math.min(1, y)),
            };

            this.renderMarkers();

            // Dispatch event for parent component to handle
            this.$dispatch('annotation-placed', {
                page: this.currentPage,
                x: this.pendingAnnotation.x,
                y: this.pendingAnnotation.y,
            });
        },

        selectComment(commentId) {
            this.selectedCommentId = commentId;
            this.renderMarkers();

            // Dispatch event for scrolling to comment in sidebar
            this.$dispatch('comment-selected', { commentId });
        },

        highlightComment(commentId) {
            const comment = this.comments.find(c => c.id === commentId);
            if (!comment || !comment.location_data) return;

            // Navigate to the comment's page if needed
            if (comment.location_data.page !== this.currentPage) {
                this.goToPage(comment.location_data.page);
            }

            this.selectedCommentId = commentId;
            this.renderMarkers();
        },

        toggleAnnotationMode() {
            this.annotationMode = !this.annotationMode;
            if (!this.annotationMode) {
                this.pendingAnnotation = null;
                this.renderMarkers();
            }
        },

        cancelPendingAnnotation() {
            this.pendingAnnotation = null;
            this.renderMarkers();
            this.$dispatch('annotation-cancelled');
        },

        confirmAnnotation() {
            if (!this.pendingAnnotation) return;

            this.$dispatch('annotation-confirmed', {
                page: this.pendingAnnotation.page,
                x: this.pendingAnnotation.x,
                y: this.pendingAnnotation.y,
            });
        },

        // Navigation
        goToPage(pageNum) {
            if (pageNum < 1 || pageNum > this.totalPages) return;
            this.currentPage = pageNum;
            this.renderPage(this.currentPage);
        },

        prevPage() {
            if (this.currentPage <= 1) return;
            this.goToPage(this.currentPage - 1);
        },

        nextPage() {
            if (this.currentPage >= this.totalPages) return;
            this.goToPage(this.currentPage + 1);
        },

        // Zoom
        zoomIn() {
            if (this.scale >= this.maxScale) return;
            this.scale = Math.min(this.maxScale, this.scale + 0.25);
            this.renderPage(this.currentPage);
        },

        zoomOut() {
            if (this.scale <= this.minScale) return;
            this.scale = Math.max(this.minScale, this.scale - 0.25);
            this.renderPage(this.currentPage);
        },

        resetZoom() {
            this.scale = 1.0;
            this.renderPage(this.currentPage);
        },

        // URL refresh handling
        setupUrlRefresh() {
            if (!this.urlExpiresAt || !this.refreshUrlEndpoint) return;

            const checkInterval = setInterval(() => {
                const now = Math.floor(Date.now() / 1000);
                const timeUntilExpiry = this.urlExpiresAt - now;

                // Refresh 5 minutes before expiry
                if (timeUntilExpiry < 300) {
                    this.refreshUrl();
                }
            }, 60000); // Check every minute

            // Clean up on component destroy
            this.$cleanup = () => {
                clearInterval(checkInterval);
            };
        },

        async refreshUrl() {
            if (!this.refreshUrlEndpoint) return;

            try {
                const response = await fetch(this.refreshUrlEndpoint, {
                    headers: {
                        'X-CSRF-TOKEN': this.csrfToken,
                        'Accept': 'application/json',
                    },
                });

                if (!response.ok) throw new Error('Failed to refresh URL');

                const data = await response.json();
                this.pdfUrl = data.url;
                this.urlExpiresAt = Math.floor(Date.now() / 1000) + data.expires_in;

                // Reload the PDF with new URL
                await this.loadPdf();
            } catch (error) {
                console.error('Error refreshing PDF URL:', error);
            }
        },

        // Update comments from external source
        updateComments(newComments) {
            this.comments = newComments;
            this.renderMarkers();
        },

        // Get marker count for a specific page
        getPageMarkerCount(pageNum) {
            return this.comments.filter(comment => {
                if (!comment.location_data) return false;
                return comment.location_data.page === pageNum &&
                       comment.location_data.upload_id === this.uploadId;
            }).length;
        },
    };
}
